import React from 'react';
import {
    McButton,
    McIcon,
    McList,
    McListItem,
    McTag,
} from '@maersk-global/mds-react-wrapper';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { useApplicationAccess } from '../../hooks/useApplicationAccess';
import { useAuth } from '../../context/AuthContext';
import { roleConfig } from '../../config/roles';
import { ConfirmationDialog } from '../Common/ConfirmationDialog';
import { API_BASE_URL, QUERY_ACTIONS_BASE_URL, QUERY_EXECUTE_BASE_URL } from '../../config/api';

const getConfiguredContextPath = (rawBaseUrl?: string) => {
    if (!rawBaseUrl) {
        return '';
    }

    const normalized = rawBaseUrl.trim().replace(/\/$/, '');

    try {
        const parsedUrl = new URL(normalized);
        const withoutPortsightPrefix = parsedUrl.pathname.replace(/^\/portsight\/v1(?=\/|$)/, '');
        return withoutPortsightPrefix.replace(/\/$/, '');
    } catch {
        const withoutHost = normalized.replace(/^https?:\/\/[^/]+/i, '');
        const withoutPortsightPrefix = withoutHost.replace(/^\/portsight\/v1(?=\/|$)/, '');
        const sanitized = withoutPortsightPrefix.replace(/\/$/, '');
        if (!sanitized) {
            return '';
        }
        return sanitized.startsWith('/') ? sanitized : `/${sanitized}`;
    }
};

interface QueryDetailHeaderProps {
    query: {
        name: string;
        isActive?: boolean;
        queryId: string;
        currentVersion: string | number;
        category: string;
        applicationId?: string;
        databaseType?: string;
        connectionId?: string;
    };
    onNavigateEdit: () => void;
    onToggleActive: () => void;
    onDelete: () => void;
    onNavigateCreateSchedule: () => void;
    onToggleSidebar: () => void;
    isSidebarOpen: boolean;
    isTogglePending: boolean;
}

export const QueryDetailHeader: React.FC<QueryDetailHeaderProps> = ({
    query,
    onNavigateEdit,
    onToggleActive,
    onDelete,
    onNavigateCreateSchedule,
    onToggleSidebar,
    isSidebarOpen,
    isTogglePending,
}) => {
    const { hasAccess } = useApplicationAccess();
    const { hasRole } = useAuth();
    
    // Global permissions
    const isQueryAdmin = hasRole(roleConfig.manageQueries);
    const isScheduleAdmin = hasRole(roleConfig.adminSchedules);
    
    // Application-specific permission
    const hasEditAccess = isQueryAdmin && hasAccess(query.applicationId);
    
    const [confirmAction, setConfirmAction] = React.useState<'delete' | 'activate' | 'deactivate' | null>(null);
    const [curlMenuOpen, setCurlMenuOpen] = React.useState(false);
    const [infoOpen, setInfoOpen] = React.useState(false);
    const infoRef = React.useRef<HTMLDivElement | null>(null);
    const [copiedKey, setCopiedKey] = React.useState<'current' | 'version' | null>(null);
    const curlMenuRef = React.useRef<HTMLDivElement | null>(null);

    React.useEffect(() => {
        if (!curlMenuOpen) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (curlMenuRef.current && !curlMenuRef.current.contains(target)) {
                setCurlMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [curlMenuOpen]);

    React.useEffect(() => {
        if (!infoOpen) return;
        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (infoRef.current && !infoRef.current.contains(target)) {
                setInfoOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [infoOpen]);

    const buildCurl = (versionNumber?: string | number) => {
        const contextPath = getConfiguredContextPath(QUERY_ACTIONS_BASE_URL || API_BASE_URL || '');
        const baseUrl = `${QUERY_EXECUTE_BASE_URL}${contextPath}`.replace(/\/$/, '');
        const body: Record<string, any> = {
            terminalId: 'ESALG',
            returnResults: true,
            maxResults: 50,
        };
        const endpoint = versionNumber != null
            ? `${baseUrl}/queries/${query.queryId}/versions/${versionNumber}/execute`
            : `${baseUrl}/queries/${query.queryId}/execute`;
        const formattedBody = JSON.stringify(body, null, 2);
        return [
            `curl --request POST \\`,
            `  --url ${endpoint} \\`,
            `  --header 'authorization: Bearer <TOKEN>' \\`,
            `  --header 'content-type: application/json' \\`,
            `  --data '${formattedBody}'`,
        ].join('\n');
    };

    const handleCopyCurrent = () => {
        navigator.clipboard.writeText(buildCurl()).then(() => {
            setCopiedKey('current');
            setCurlMenuOpen(false);
            setTimeout(() => setCopiedKey(null), 2000);
        });
    };

    const handleCopyVersion = () => {
        navigator.clipboard.writeText(buildCurl(query.currentVersion)).then(() => {
            setCopiedKey('version');
            setCurlMenuOpen(false);
            setTimeout(() => setCopiedKey(null), 2000);
        });
    };

    const handleConfirm = () => {
        if (confirmAction === 'delete') onDelete();
        else if (confirmAction === 'activate' || confirmAction === 'deactivate') onToggleActive();
        setConfirmAction(null);
    };

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2px', flexWrap: 'wrap', gap: '6px' }}>
                <div style={{ minWidth: '300px', flex: '1' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '0px' }}>
                        <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                            <div slot="trigger">
                                <McButton
                                    variant={isSidebarOpen ? 'filled' : 'outlined'}
                                    appearance={isSidebarOpen ? 'primary' : 'neutral'}
                                    fit="small"
                                    onClick={onToggleSidebar}
                                    style={{ minWidth: 34, padding: '0 6px' }}
                                >
                                    <McIcon icon="sliders-horizontal" />
                                </McButton>
                            </div>
                            <span>{isSidebarOpen ? 'Hide queries' : 'Show queries'}</span>
                        </McTooltip>
                        <h1 style={{ fontSize: 16, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '520px', margin: 0 }}>{query.name}</h1>
                        <McTag
                            label={query.isActive ? 'Active' : 'Inactive'}
                            appearance={query.isActive ? 'success' : 'neutral'}
                        />
                        <div ref={infoRef} style={{ position: 'relative', display: 'flex', alignItems: 'center', height: '100%' }}>
                            <McButton variant="plain" appearance="secondary" fit="small" 
                                onClick={() => setInfoOpen(prev => !prev)} 
                                style={{ minWidth: 32, padding: '0 4px', height: 32, color: 'var(--mds-color-neutral-weaker-text, #6b7280)' }}>
                                <McIcon icon="shield" style={{ fontSize: '18px' }} />
                            </McButton>
                            {infoOpen && (
                                <div style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1000,
                                    minWidth: '220px',
                                    background: 'var(--mds-color-neutral-canvas-background, #fff)',
                                    border: '1px solid var(--mds-color-neutral-weak-decoration)',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                    padding: '12px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '8px',
                                    fontSize: '14px',
                                    color: 'var(--mds-color-text-color-primary, #111)'
                                }}>
                                    <div><strong>ID:</strong> {query.queryId}</div>
                                    <div><strong>Version:</strong> {query.currentVersion}</div>
                                    <div><strong>Category:</strong> {query.category}</div>
                                    {query.applicationId && (
                                        <div><strong>Application:</strong> {query.applicationId}</div>
                                    )}
                                    {query.databaseType && (
                                        <div><strong>DB Type:</strong> {query.databaseType}</div>
                                    )}
                                    {query.connectionId && (
                                        <div><strong>Connection:</strong> {query.connectionId}</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>

                    <div ref={curlMenuRef} style={{ position: 'relative' }}>
                        <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-left">
                            <div slot="trigger">
                                <McButton
                                    variant="plain"
                                    appearance="secondary"
                                    fit="small"
                                    onClick={() => setCurlMenuOpen((prev) => !prev)}
                                    style={{ minWidth: 36, padding: '0 8px' }}
                                >
                                    <McIcon icon={copiedKey ? 'check' : 'copy'} />
                                </McButton>
                            </div>
                            <span>{copiedKey === 'current' ? 'Current command copied!' : copiedKey === 'version' ? 'Version command copied!' : 'API Command'}</span>
                        </McTooltip>
                        {curlMenuOpen && (
                            <div
                                style={{
                                    position: 'absolute',
                                    top: 'calc(100% + 4px)',
                                    left: 0,
                                    zIndex: 1000,
                                    minWidth: '320px',
                                    background: 'var(--mds-color-neutral-canvas-background, #fff)',
                                    border: '1px solid var(--mds-color-neutral-weak-decoration)',
                                    borderRadius: '8px',
                                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                                    padding: '4px',
                                }}
                            >
                                <McList>
                                    <McListItem
                                        label="Copy API command - Current active version"
                                        icon="copy"
                                        onClick={handleCopyCurrent}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    <McListItem
                                        label={`Copy API command - Version ${query.currentVersion} (specific)`}
                                        icon="copy"
                                        onClick={handleCopyVersion}
                                        style={{ cursor: 'pointer' }}
                                    />
                                </McList>
                            </div>
                        )}
                    </div>

                    {hasEditAccess ? (
                        <>
                            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                                <div slot="trigger">
                                    <McButton variant="plain" appearance="primary" fit="small" onClick={onNavigateEdit} style={{ minWidth: 36, padding: '0 8px' }}>
                                        <McIcon icon="pencil" />
                                    </McButton>
                                </div>
                                <span>Edit query</span>
                            </McTooltip>

                            {isScheduleAdmin && (
                                <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                                    <div slot="trigger">
                                        <McButton variant="plain" appearance="primary" fit="small" onClick={onNavigateCreateSchedule} style={{ minWidth: 36, padding: '0 8px' }}>
                                            <McIcon icon="calendar" />
                                        </McButton>
                                    </div>
                                    <span>Create schedule</span>
                                </McTooltip>
                            )}

                            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                                <div slot="trigger">
                                    <McButton
                                        variant="plain"
                                        appearance={query.isActive ? 'neutral' : 'primary'}
                                        fit="small"
                                        onClick={() => setConfirmAction(query.isActive ? 'deactivate' : 'activate')}
                                        disabled={isTogglePending}
                                        style={{ minWidth: 36, padding: '0 8px' }}
                                    >
                                        <McIcon icon='eye' />
                                    </McButton>
                                </div>
                                <span>{query.isActive ? 'Deactivate query' : 'Activate query'}</span>
                            </McTooltip>

                            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                                <div slot="trigger">
                                    <McButton
                                        variant="plain"
                                        appearance="error"
                                        fit="small"
                                        onClick={() => setConfirmAction('delete')}
                                        style={{ minWidth: 36, padding: '0 8px' }}
                                    >
                                        <McIcon icon="trash" />
                                    </McButton>
                                </div>
                                <span>Delete query</span>
                            </McTooltip>
                        </>
                    ) : (
                        <div style={{ display: 'flex', alignItems: 'center', color: 'var(--mds-color-text-color-secondary)', fontSize: '0.875rem' }}>
                            <McIcon icon="lock" style={{ marginRight: '4px' }} />
                            <span>Read Only ({query.applicationId})</span>
                        </div>
                    )}
                </div>
            </div>

            <ConfirmationDialog
                open={!!confirmAction}
                onClose={() => setConfirmAction(null)}
                onConfirm={handleConfirm}
                title={confirmAction === 'delete' ? 'Delete Query' : confirmAction === 'activate' ? 'Activate Query' : 'Deactivate Query'}
                content={
                    confirmAction === 'delete' ? (
                        <p>Are you sure you want to <strong>delete</strong> the query "{query.name}"? This action cannot be undone.</p>
                    ) : (
                        <p>Are you sure you want to <strong>{confirmAction}</strong> the query "{query.name}"?</p>
                    )
                }
                confirmLabel={confirmAction === 'delete' ? 'Delete' : 'Confirm'}
                variant={confirmAction === 'delete' ? 'danger' : 'primary'}
            />
        </>
    );
};
